import { useEffect, useRef, useCallback } from "react"
import Api from "@ciq/shared/api/api"
import eventBus from "@ciq/shared/hooks/eventBus"
import { EVENT_BUS_EVENTS } from "@ciq/shared/hooks/eventBusEvents"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { useMapData } from "../useMapData"
import { useAppContext } from "../../../../../hooks/context"
import { translateFloorPlanCoordinates, computeFanOffset } from "../../../utils/floorPlanCoordinates"
import { loadFloorPlanImage } from "../../../utils/loadFloorPlanImage"
import { FlatMapKeys, FAN_OUTLINE_DEFAULT_WIDTH } from "./flat_source"
import { ZoneType } from "@ciq/shared/utils/const"

const FLOOR_PLAN_LAYER_PREFIX = "da-floor-plan-"
const FLOOR_PLAN_OPACITY = 0.8

export const CONTRAST_OUTLINE_LAYER_ID = "da-floor-plan-zone-contrast-outline"

export const CONTRAST_OUTLINE_PAINT = {
  "line-color": "#FFFFFF",
  "line-width": ["interpolate", ["linear"], ["zoom"], 15, 1, 20, 3],
}

export const BOOSTED_OUTLINE_WIDTH = ["interpolate", ["linear"], ["zoom"], 15, 1.5, 20, 3]

/**
 * Build a unique Mapbox layer/source ID for a floor plan file.
 * @param {string} fileId - The uploaded file ID
 * @returns {string} Mapbox-safe ID
 */
export function buildFloorPlanLayerId(fileId) {
  return `${FLOOR_PLAN_LAYER_PREFIX}${fileId}`
}

/**
 * Resolve the building zone ID from any selected resource by walking up the parent chain.
 * The zone table's `building_id` column points to the campus, NOT the building zone.
 * So for floors/rooms, we walk up via getParent until we find a Building-type zone.
 *
 * @param {Object|null} selectedResource - Currently selected zone
 * @param {Function} getParent - Function to get parent zone from useMapData
 * @returns {string|null} Building zone ID or null
 */
export function resolveBuildingZoneId(selectedResource, getParent) {
  if (!selectedResource?.id) return null
  if (selectedResource.type === ZoneType.BUILDING) return selectedResource.id

  let current = selectedResource
  while (current) {
    const parent = getParent?.(current)
    if (!parent) return null
    if (parent.type === ZoneType.BUILDING) return parent.id
    current = parent
  }
  return null
}

/**
 * @typedef {Object} PlacedFloorPlan
 * @property {string} id - Uploaded file ID
 * @property {string} placement_status - File placement status ("placed")
 * @property {Array<Array<number>>} coordinates - 4 corner coordinates [lng, lat]
 * @property {string} placed_on_zone_id - Floor zone ID the plan is placed on
 * @property {string} building_zone_id - Building zone ID the plan belongs to
 */

/**
 * Fetch placed floor plans for a building from the API.
 * Fetches all placed files for the school, then filters client-side by buildingZoneId.
 *
 * @param {string} schoolId - School UUID
 * @param {string} buildingZoneId - Building zone UUID
 * @param {AbortSignal|null} signal - Optional abort signal
 * @returns {Promise<Array<PlacedFloorPlan>>} Array of placed file objects with coordinates
 */
export async function fetchPlacedFloorPlans(schoolId, buildingZoneId, signal) {
  try {
    const response = await Api.get(
      `schools/${schoolId}/uploaded-files?placement_status=placed`,
      { signal }
    )
    const files = response?.data?.files || []
    return files.filter(f =>
      f.placement_status === "placed" &&
      f.coordinates &&
      f.building_zone_id === buildingZoneId
    )
  } catch (e) {
    if (e?.error?.code === "ERR_CANCELED" || signal?.aborted) {
      return []
    }
    console.error("Failed to fetch floor plans:", e)
    ErrorToast("Failed to load floor plans")
    return []
  }
}

/**
 * Get presigned download URL for a floor plan file.
 *
 * @param {string} schoolId - School UUID
 * @param {string} fileId - File UUID
 * @param {AbortSignal|null} signal - Optional abort signal
 * @returns {Promise<string|null>} Download URL or null
 */
export async function getDownloadUrl(schoolId, fileId, signal) {
  try {
    const response = await Api.get(
      `schools/${schoolId}/uploaded-files/${fileId}/download-url?version=rendered`,
      { signal }
    )
    return response?.data?.download_url || null
  } catch (e) {
    if (e?.error?.code === "ERR_CANCELED" || signal?.aborted) return null
    // Fall back to original version when rendered is not yet available (404)
    if (e?.error?.response?.status === 404) {
      try {
        const response = await Api.get(
          `schools/${schoolId}/uploaded-files/${fileId}/download-url?version=original`,
          { signal }
        )
        // Skip non-image originals (e.g. PDFs) — they can't render as map images
        const contentType = response?.data?.content_type || ""
        if (contentType && !contentType.startsWith("image/")) return null
        return response?.data?.download_url || null
      } catch (fallbackError) {
        if (fallbackError?.error?.code === "ERR_CANCELED" || signal?.aborted) return null
        console.error("Failed to get floor plan download URL:", fallbackError)
        return null
      }
    }
    // No toast here — the prefetch loop aggregates failures into a single toast via failedCount
    console.error("Failed to get floor plan download URL:", e)
    return null
  }
}

/**
 * Add a floor plan image source + raster layer to the map.
 *
 * @param {Object} map - Mapbox GL map instance
 * @param {string} sourceId - Unique source ID
 * @param {string} imageUrl - Processed image URL (blob URL or raw image URL)
 * @param {Array<Array<number>>} coordinates - 4 corner coordinates [lng, lat]
 * @param {string} insertBelowLayerId - Layer to insert below
 */
export function addFloorPlanLayer(map, sourceId, imageUrl, coordinates, insertBelowLayerId) {
  if (map.getSource(sourceId)) return

  // Image source: renders processed floor plan as a geo-positioned raster overlay
  map.addSource(sourceId, {
    type: "image",
    url: imageUrl,
    coordinates,
  })

  // Only use insertBelowLayerId if the layer exists on the map
  const beforeId = insertBelowLayerId && map.getLayer(insertBelowLayerId)
    ? insertBelowLayerId
    : undefined

  map.addLayer(
    {
      id: sourceId,
      type: "raster",
      source: sourceId,
      paint: {
        "raster-opacity": FLOOR_PLAN_OPACITY,
        "raster-fade-duration": 0,
      },
    },
    beforeId
  )
}

/**
 * Remove a floor plan source + layer from the map.
 *
 * @param {Object} map - Mapbox GL map instance
 * @param {string} sourceId - Source/layer ID to remove
 */
export function removeFloorPlanLayer(map, sourceId) {
  if (map.getLayer(sourceId)) {
    map.removeLayer(sourceId)
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId)
  }
}

function revokeBlobUrlIfNeeded(entry) {
  if (entry?.isBlobUrl) {
    URL.revokeObjectURL(entry.url)
  }
}

/**
 * Add the zone contrast outline layer to the map.
 * Uses the fan source for zone boundary data. Inserted before insertBelowLayerId
 * so it renders above floor plans but below selection outlines.
 *
 * @param {Object} map - Mapbox GL map instance
 * @param {string} insertBelowLayerId - Layer to insert before (typically fan outline)
 */
export function addContrastOutlineLayer(map, insertBelowLayerId) {
  if (map.getLayer(CONTRAST_OUTLINE_LAYER_ID)) return
  if (!map.getSource(FlatMapKeys.FAN_SOURCE_ID)) return

  const beforeId = insertBelowLayerId && map.getLayer(insertBelowLayerId)
    ? insertBelowLayerId
    : undefined

  map.addLayer(
    {
      id: CONTRAST_OUTLINE_LAYER_ID,
      type: "line",
      source: FlatMapKeys.FAN_SOURCE_ID,
      paint: CONTRAST_OUTLINE_PAINT,
    },
    beforeId
  )
}

/**
 * Remove the zone contrast outline layer from the map.
 *
 * @param {Object} map - Mapbox GL map instance
 */
export function removeContrastOutlineLayer(map) {
  if (map.getLayer(CONTRAST_OUTLINE_LAYER_ID)) {
    map.removeLayer(CONTRAST_OUTLINE_LAYER_ID)
  }
}

/**
 * @typedef {Object} FloorPlanEntry
 * @property {string} fileId - Uploaded file ID
 * @property {string} sourceId - Mapbox source/layer ID
 * @property {string} zoneId - Floor zone ID the plan is placed on
 */

/**
 * FloorPlanSource — React component (returns null) that manages floor plan
 * image rendering on fanned floor polygons in Map View.
 *
 * Lifecycle:
 * 1. When a building is selected and floors fan out, fetches placed files
 * 2. Prefetches and processes images (white halo)
 * 3. On toggle (Shift+F / eventBus), shows/hides layers
 * 4. On building deselect, removes all floor plan layers
 *
 * @param {Object} props
 * @param {Object} props.map - Mapbox GL map instance
 * @param {boolean} props.isMapLoading - Whether map is still loading
 * @returns {null}
 */
export function FloorPlanSource({ map, isMapLoading, zoneContrastEnabled = false }) {
  const { schoolId } = useAppContext()
  const { selectedResource, geojson, getParent } = useMapData()
  const flatGeojson = geojson?.flat
  const fanGeojson = geojson?.fan
  const activeLayersRef = useRef([])
  const abortRef = useRef(null)
  const visibleRef = useRef(false)
  const prefetchedRef = useRef(new Map()) // fileId -> { url, isBlobUrl, translatedCoords, zoneId }
  const zoneContrastRef = useRef(zoneContrastEnabled)
  zoneContrastRef.current = zoneContrastEnabled

  const buildingId = resolveBuildingZoneId(selectedResource, getParent)

  // Clean up all floor plan layers
  const cleanup = useCallback(() => {
    if (!map) return
    for (const entry of activeLayersRef.current) {
      removeFloorPlanLayer(map, entry.sourceId)
    }
    activeLayersRef.current = []
    for (const [, data] of prefetchedRef.current) {
      revokeBlobUrlIfNeeded(data)
    }
    prefetchedRef.current.clear()
    visibleRef.current = false
    removeContrastOutlineLayer(map)
    if (zoneContrastRef.current && map.getLayer(FlatMapKeys.Layers.FAN_OUTLINE)) {
      map.setPaintProperty(
        FlatMapKeys.Layers.FAN_OUTLINE,
        "line-width",
        FAN_OUTLINE_DEFAULT_WIDTH
      )
    }
  }, [map])

  // Show floor plan layers on the map
  const showFloorPlans = useCallback(() => {
    if (!map) return

    // Insert below the fan outline layer so outlines remain visible above floor plans
    const insertBelow = FlatMapKeys.Layers.FAN_OUTLINE

    // Clear tracking array to prevent duplicate accumulation when showFloorPlans
    // is called multiple times (e.g., mid-prefetch toggle then prefetch completion)
    activeLayersRef.current = []

    for (const [fileId, data] of prefetchedRef.current) {
      const sourceId = buildFloorPlanLayerId(fileId)
      addFloorPlanLayer(map, sourceId, data.url, data.translatedCoords, insertBelow)
      activeLayersRef.current.push({ fileId, sourceId, zoneId: data.zoneId })
    }
  }, [map])

  // Hide floor plan layers
  const hideFloorPlans = useCallback(() => {
    if (!map) return
    for (const entry of activeLayersRef.current) {
      removeFloorPlanLayer(map, entry.sourceId)
    }
    activeLayersRef.current = []
  }, [map])

  // Prefetch floor plans when building is selected (before toggle)
  useEffect(() => {
    if (!map || isMapLoading || !schoolId || !buildingId || !fanGeojson?.features?.length) {
      cleanup()
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const prefetch = async () => {
      const placedFiles = await fetchPlacedFloorPlans(schoolId, buildingId, controller.signal)
      if (controller.signal.aborted) return

      if (placedFiles.length === 0) {
        prefetchedRef.current.clear()
        return
      }

      let failedCount = 0
      for (const file of placedFiles) {
        if (controller.signal.aborted) return

        const url = await getDownloadUrl(schoolId, file.id, controller.signal)
        if (controller.signal.aborted) continue
        if (!url) { failedCount++; continue }

        let imageResult
        try {
          imageResult = await loadFloorPlanImage(url, controller.signal, { useHalo: false })
        } catch (e) {
          if (e.name === "AbortError") continue
          failedCount++
          continue
        }
        if (controller.signal.aborted) {
          revokeBlobUrlIfNeeded(imageResult)
          continue
        }
        // loadFloorPlanImage falls back to raw URL on processing failure (CORS, canvas, load error).
        // Count as failure — the raw presigned URL may render unprocessed, but if the image itself
        // failed to load, Mapbox will also fail. Either way, surface the failure count.
        if (!imageResult.isBlobUrl) {
          failedCount++
          continue
        }

        // Find the original floor polygon from the flat (base) geojson
        // Floor plan coordinates are placed relative to this floor's position
        const originalFloorFeature = flatGeojson?.features?.find(
          f => f.properties?.id === file.placed_on_zone_id
        )

        // Find the fan floor polygon for this file's zone
        const fanFloorFeature = fanGeojson.features.find(
          f => f.properties?.id === file.placed_on_zone_id
        )

        const offset = computeFanOffset(
          originalFloorFeature?.geometry,
          fanFloorFeature?.geometry
        )
        const translatedCoords = translateFloorPlanCoordinates(file.coordinates, offset)
        if (!translatedCoords) {
          revokeBlobUrlIfNeeded(imageResult)
          continue
        }

        const existing = prefetchedRef.current.get(file.id)
        revokeBlobUrlIfNeeded(existing)
        prefetchedRef.current.set(file.id, {
          url: imageResult.url,
          isBlobUrl: imageResult.isBlobUrl,
          translatedCoords,
          zoneId: file.placed_on_zone_id,
        })
      }

      if (failedCount > 0 && !controller.signal.aborted) {
        ErrorToast(`${failedCount} floor plan(s) failed to load`)
      }

      // If visibility is already on (e.g., toggle was activated during load), show immediately
      if (visibleRef.current) {
        showFloorPlans()
        if (zoneContrastRef.current) {
          addContrastOutlineLayer(map, FlatMapKeys.Layers.FAN_OUTLINE)
          if (map.getLayer(FlatMapKeys.Layers.FAN_OUTLINE)) {
            map.setPaintProperty(
              FlatMapKeys.Layers.FAN_OUTLINE,
              "line-width",
              BOOSTED_OUTLINE_WIDTH
            )
          }
        }
      }
    }

    prefetch()

    return () => {
      controller.abort()
      cleanup()
    }
  }, [map, isMapLoading, schoolId, buildingId, flatGeojson, fanGeojson, cleanup, showFloorPlans])

  // Listen for floor plan visibility toggle
  useEffect(() => {
    const handleVisibilityChange = (visible) => {
      visibleRef.current = visible
      if (visible) {
        if (prefetchedRef.current.size === 0) {
          ErrorToast("No floor plan placed")
          return
        }
        showFloorPlans()
        if (zoneContrastRef.current) {
          addContrastOutlineLayer(map, FlatMapKeys.Layers.FAN_OUTLINE)
          if (map.getLayer(FlatMapKeys.Layers.FAN_OUTLINE)) {
            map.setPaintProperty(
              FlatMapKeys.Layers.FAN_OUTLINE,
              "line-width",
              BOOSTED_OUTLINE_WIDTH
            )
          }
        }
      } else {
        hideFloorPlans()
        removeContrastOutlineLayer(map)
        if (zoneContrastRef.current && map.getLayer(FlatMapKeys.Layers.FAN_OUTLINE)) {
          map.setPaintProperty(
            FlatMapKeys.Layers.FAN_OUTLINE,
            "line-width",
            FAN_OUTLINE_DEFAULT_WIDTH
          )
        }
      }
    }

    eventBus.on(EVENT_BUS_EVENTS.FLOOR_PLAN_VISIBILITY_CHANGED, handleVisibilityChange)
    return () => {
      eventBus.off(EVENT_BUS_EVENTS.FLOOR_PLAN_VISIBILITY_CHANGED, handleVisibilityChange)
    }
  }, [map, showFloorPlans, hideFloorPlans])

  return null
}
